import React, { Component } from "react";
import { urlMap, getAuthHeaders } from "../../../utils";
import PomodoroSessionCard from "./PomodoroSessionCard";

const TASKS_PER_PAGE = 5;

/**
 * Modal that shows the writer-session history for the current chapter.
 * Fetches sessions on open. Closes on backdrop click or Escape.
 */
class PomodoroSessionsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      sessions: [],
      deletingSessionId: null,
      reflections: {}, // Map of sessionId => reflections array
      expandedSessions: new Set(), // Track which sessions have expanded reflections
      loadingReflections: new Set(), // Track which sessions are loading reflections
      activeTab: "sessions",
      progressPoints: [],
      progressLoaded: false,
      loadingProgress: false,
      progressError: null,
      selectedProgressPoint: null,
      progressDate: new Date(),
      progressZoom: 1,
      tasksPage: 1,
    };
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown);
    this.fetchSessions();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.chapterId !== this.props.chapterId) {
      this.setState({
        activeTab: "sessions",
        reflections: {},
        expandedSessions: new Set(),
        progressPoints: [],
        progressLoaded: false,
        progressError: null,
        selectedProgressPoint: null,
        progressDate: new Date(),
        progressZoom: 1,
        tasksPage: 1,
      }, this.fetchSessions);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      this.props.onClose?.();
    }
  };

  handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      this.props.onClose?.();
    }
  };

  fetchSessions = async () => {
    const { chapterId } = this.props;
    if (!chapterId) return;
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(urlMap.listWriterSessions(chapterId), {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const sessions = await res.json();
      const normalizedSessions = Array.isArray(sessions) ? sessions : [];
      const reflectionEntries = await Promise.all(normalizedSessions.map(async (session) => {
        try {
          const reflectionRes = await fetch(urlMap.listWriterSessionReflections(session.id), {
            headers: { ...getAuthHeaders() },
          });
          if (!reflectionRes.ok) throw new Error(`HTTP ${reflectionRes.status}`);
          const reflections = await reflectionRes.json();
          return [session.id, Array.isArray(reflections) ? reflections : []];
        } catch {
          return [session.id, []];
        }
      }));
      this.setState({
        sessions: normalizedSessions,
        reflections: Object.fromEntries(reflectionEntries),
        loading: false,
        tasksPage: 1,
      });
    } catch (err) {
      this.setState({ error: err?.message || "Failed to load sessions", loading: false });
    }
  };

  formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  formatDuration = (secondsInput) => {
    const totalSeconds = Math.max(0, Number(secondsInput) || 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${String(seconds).padStart(2, "0")}s`;
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  };

  confirmDelete = (sessionId) => {
    this.setState({ deletingSessionId: sessionId });
  };

  cancelDelete = () => {
    this.setState({ deletingSessionId: null });
  };

  deleteSession = async (sessionId) => {
    try {
      const res = await fetch(urlMap.deleteWriterSession(sessionId), {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.setState((s) => ({
        sessions: s.sessions.filter((sess) => sess.id !== sessionId),
        deletingSessionId: null,
      }));
    } catch (err) {
      this.setState({ error: err?.message || 'Failed to delete session', deletingSessionId: null });
    }
  };

  togglePublishReflection = async (sessionId, reflectionId, nextStatus) => {
    if (!sessionId || !reflectionId) return;
    try {
      const res = await fetch(urlMap.updateReflectionStatus(sessionId, reflectionId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      this.setState((s) => ({
        reflections: {
          ...s.reflections,
          [sessionId]: (s.reflections[sessionId] || []).map((reflection) => (
            reflection.id === reflectionId ? { ...reflection, status: updated.status } : reflection
          )),
        },
      }));
    } catch (err) {
      this.setState({ error: err?.message || 'Failed to update reflection status' });
    }
  };

  fetchReflectionsForSession = async (sessionId) => {
    this.setState((s) => ({
      loadingReflections: new Set([...s.loadingReflections, sessionId]),
    }));
    try {
      const res = await fetch(urlMap.listWriterSessionReflections(sessionId), {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reflections = await res.json();
      this.setState((s) => ({
        reflections: {
          ...s.reflections,
          [sessionId]: Array.isArray(reflections) ? reflections : [],
        },
        loadingReflections: new Set([...s.loadingReflections].filter(id => id !== sessionId)),
      }));
    } catch (err) {
      console.warn(`Failed to load reflections for session ${sessionId}:`, err);
      this.setState((s) => ({
        reflections: {
          ...s.reflections,
          [sessionId]: [],
        },
        loadingReflections: new Set([...s.loadingReflections].filter(id => id !== sessionId)),
      }));
    }
  };

  fetchProgress = async () => {
    const { chapterId } = this.props;
    if (!chapterId || this.state.loadingProgress) return;

    this.setState({ loadingProgress: true, progressError: null });
    try {
      const res = await fetch(urlMap.listChapterReflectionProgress(chapterId), {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const progressPoints = await res.json();
      const normalizedPoints = Array.isArray(progressPoints) ? progressPoints : [];
      const latestPoint = normalizedPoints[normalizedPoints.length - 1];
      this.setState({
        progressPoints: normalizedPoints,
        progressDate: latestPoint ? new Date(latestPoint.created_at) : new Date(),
        progressLoaded: true,
        loadingProgress: false,
      });
    } catch (err) {
      this.setState({
        progressError: err?.message || "Failed to load reflection progress",
        loadingProgress: false,
      });
    }
  };

  selectTab = (activeTab) => {
    this.setState({ activeTab });
    if (activeTab === "progress" && !this.state.progressLoaded) {
      this.fetchProgress();
    }
  };

  selectProgressPoint = async (point) => {
    this.setState({ selectedProgressPoint: point });
    await this.fetchReflectionsForSession(point.writer_session_id);
  };

  moveTasksPage = (direction) => {
    this.setState((state) => ({ tasksPage: Math.max(1, state.tasksPage + direction) }));
  };

  moveProgressPeriod = (direction) => {
    this.setState((state) => {
      const progressDate = new Date(state.progressDate);
      progressDate.setMonth(progressDate.getMonth() + direction, 1);
      return { progressDate, selectedProgressPoint: null };
    });
  };

  setProgressZoom = (event) => {
    this.setState({ progressZoom: Number(event.target.value) || 1 });
  };

  toggleReflectionExpanded = (sessionId) => {
    this.setState((s) => {
      const expanded = new Set(s.expandedSessions);
      if (expanded.has(sessionId)) {
        expanded.delete(sessionId);
      } else {
        expanded.add(sessionId);
        // Fetch reflections if not already loaded
        if (!s.reflections[sessionId]) {
          this.fetchReflectionsForSession(sessionId);
        }
      }
      return { expandedSessions: expanded };
    });
  };

  render() {
    const { onClose, chapterTitle, embedded = false } = this.props;
    const {
      loading, error, sessions, activeTab, progressPoints, selectedProgressPoint,
      progressDate, progressZoom, tasksPage,
    } = this.state;

    const issueGroups = sessions.reduce((groups, session) => {
      const sessionReflections = this.state.reflections[session.id] || [];
      if (sessionReflections.length === 0) {
        const issueKey = "free-style";
        const existing = groups.get(issueKey) || {
          id: issueKey,
          title: "Free style",
          description: "Writing sessions without an assigned issue.",
          entries: [],
          sessionIds: new Set(),
          totalSeconds: 0,
        };
        existing.entries.push({ session, reflection: null });
        if (!existing.sessionIds.has(session.id)) {
          existing.sessionIds.add(session.id);
          existing.totalSeconds += Number(session.actual_writing_seconds) || 0;
        }
        groups.set(issueKey, existing);
      }
      sessionReflections.forEach((reflection) => {
        const issueTitle = String(reflection.issue_title || "").trim();
        const normalizedIssueTitle = issueTitle.toLocaleLowerCase().replace(/\s+/g, " ");
        const issueKey = reflection.issue_id
          ? `task:${normalizedIssueTitle || reflection.issue_id}`
          : "free-style";
        const existing = groups.get(issueKey) || {
          id: issueKey,
          title: reflection.issue_id ? (issueTitle || "Untitled issue") : "Free style",
          description: reflection.issue_id
            ? (reflection.issue_description || "No description available")
            : "Writing sessions without an assigned issue.",
          entries: [],
          sessionIds: new Set(),
          totalSeconds: 0,
        };
        existing.entries.push({ session, reflection });
        if (!existing.sessionIds.has(session.id)) {
          existing.sessionIds.add(session.id);
          existing.totalSeconds += Number(session.actual_writing_seconds) || 0;
        }
        groups.set(issueKey, existing);
      });
      return groups;
    }, new Map());
    const groupedIssues = Array.from(issueGroups.values());
    const totalTaskPages = Math.max(1, Math.ceil(groupedIssues.length / TASKS_PER_PAGE));
    const currentTasksPage = Math.min(tasksPage, totalTaskPages);
    const visibleIssues = groupedIssues.slice(
      (currentTasksPage - 1) * TASKS_PER_PAGE,
      currentTasksPage * TASKS_PER_PAGE,
    );

    const selectedReflection = selectedProgressPoint
      ? (this.state.reflections[selectedProgressPoint.writer_session_id] || [])
        .find((reflection) => reflection.id === selectedProgressPoint.id)
      : null;
    const selectedSession = selectedProgressPoint
      ? sessions.find((session) => session.id === selectedProgressPoint.writer_session_id)
      : null;
    const chartTop = 34;
    const chartBottom = 190;
    const chartLeft = 54;
    const visibleYear = progressDate.getFullYear();
    const visibleMonth = progressDate.getMonth();
    const monthStart = new Date(visibleYear, visibleMonth, 1);
    const monthEnd = new Date(visibleYear, visibleMonth + 1, 1);
    const daysInMonth = new Date(visibleYear, visibleMonth + 1, 0).getDate();
    const monthPoints = progressPoints.filter((point) => {
      const date = new Date(point.created_at);
      return date >= monthStart && date < monthEnd;
    });
    const dayWidth = 40 * progressZoom;
    const graphWidth = Math.max(500, chartLeft + (daysInMonth * dayWidth) + 24);
    const graphHeight = 254;
    const pointPositions = new Map();
    monthPoints.forEach((point) => {
      const date = new Date(point.created_at);
      const secondsIntoDay = (date.getHours() * 3600) + (date.getMinutes() * 60) + date.getSeconds();
      pointPositions.set(point.id, {
        x: chartLeft + ((date.getDate() - 1) * dayWidth) + ((secondsIntoDay / 86400) * dayWidth),
        y: chartBottom - (((Number(point.difficulty_score) - 1) / 4) * (chartBottom - chartTop)),
      });
    });
    const hourStep = progressZoom >= 6 ? 2 : progressZoom >= 3 ? 3 : 6;
    const periodLabel = progressDate.toLocaleDateString([], { month: "long", year: "numeric" });

    const modalBody = (
      <div className={`pomodoro-modal${embedded ? " pomodoro-modal--embedded" : ""} ${activeTab === "progress" ? "pomodoro-modal--progress" : ""}`}>
          <div className="pomodoro-modal-header">
            <div className="pomodoro-modal-heading">
              <h2 className="pomodoro-modal-title">Reflections chapter: {chapterTitle || "Untitled chapter"}</h2>
              <div className="pomodoro-modal-tabs" role="tablist" aria-label="Reflection views">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "sessions"}
                  className={`pomodoro-modal-tab pomodoro-modal-tab--sessions ${activeTab === "sessions" ? "pomodoro-modal-tab--active" : ""}`}
                  onClick={() => this.selectTab("sessions")}
                >
                  <span>Sessions</span>
                  <span className="pomodoro-modal-tab-meta">{sessions.length}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "progress"}
                  className={`pomodoro-modal-tab pomodoro-modal-tab--progress ${activeTab === "progress" ? "pomodoro-modal-tab--active" : ""}`}
                  onClick={() => this.selectTab("progress")}
                >
                  <span>Progress</span>
                </button>
              </div>
            </div>
            <button
              type="button"
              className="pomodoro-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="pomodoro-modal-body">
            {activeTab === "sessions" && loading && <div className="pomodoro-modal-empty">Loading…</div>}
            {activeTab === "sessions" && error && !loading && (
              <div className="pomodoro-modal-error">Failed to load: {error}</div>
            )}
            {activeTab === "sessions" && !loading && !error && sessions.length === 0 && (
              <div className="pomodoro-modal-empty">
                No Pomodoro sessions yet. Start a sprint in writer mode to begin.
              </div>
            )}
            {activeTab === "sessions" && !loading && !error && sessions.length > 0 && (
              <div className="pomodoro-issue-groups">
                {visibleIssues.map((issue) => (
                  <details key={issue.id} className="pomodoro-issue-group">
                    <summary className="pomodoro-issue-group-summary">
                      <span className="pomodoro-issue-group-heading">
                        <strong>{issue.title}</strong>
                        <span>{issue.description}</span>
                        <span className="pomodoro-issue-group-time">Total time spent · {this.formatDuration(issue.totalSeconds)}</span>
                      </span>
                      <span className="pomodoro-issue-group-count">{issue.entries.length} reflection{issue.entries.length === 1 ? "" : "s"}</span>
                      <span className="pomodoro-issue-group-chevron" aria-hidden="true">▸</span>
                    </summary>
                    <ul className="pomodoro-session-list pomodoro-issue-group-reflections">
                      {issue.entries.map(({ session, reflection }) => (
                        <PomodoroSessionCard
                          key={reflection?.id || session.id}
                          session={session}
                          reflections={reflection ? [reflection] : []}
                          isExpanded={this.state.expandedSessions.has(session.id)}
                          onToggleExpanded={this.toggleReflectionExpanded}
                          onContinueSession={this.props.onContinueSession}
                          onDeleteSession={(sessionId) => this.confirmDelete(sessionId)}
                          onPublishToggle={this.togglePublishReflection}
                          titleOverride={`Reflection · ${this.formatDate(reflection?.created_at || session.started_at)}`}
                          groupedIssue
                          showActions
                          useAnswerExcerptAsTitle
                        />
                      ))}
                    </ul>
                  </details>
                ))}
                {groupedIssues.length > TASKS_PER_PAGE && (
                  <nav className="pomodoro-tasks-pagination" aria-label="Task pages">
                    <button type="button" onClick={() => this.moveTasksPage(-1)} disabled={currentTasksPage === 1}>
                      Previous
                    </button>
                    <span>Page {currentTasksPage} of {totalTaskPages}</span>
                    <button type="button" onClick={() => this.moveTasksPage(1)} disabled={currentTasksPage === totalTaskPages}>
                      Next
                    </button>
                  </nav>
                )}
              </div>
            )}

            {activeTab === "progress" && this.state.loadingProgress && (
              <div className="pomodoro-modal-empty">Loading progress…</div>
            )}
            {activeTab === "progress" && this.state.progressError && !this.state.loadingProgress && (
              <div className="pomodoro-modal-error">Failed to load: {this.state.progressError}</div>
            )}
            {activeTab === "progress" && !this.state.loadingProgress && !this.state.progressError && progressPoints.length === 0 && (
              <div className="pomodoro-modal-empty">No reflections with a difficulty level yet.</div>
            )}
            {activeTab === "progress" && progressPoints.length > 0 && (
              <div className="pomodoro-progress">
                <div className="pomodoro-progress-toolbar">
                  <div className="pomodoro-progress-period-nav">
                    <button type="button" onClick={() => this.moveProgressPeriod(-1)} aria-label="Previous month" title="Previous month">‹</button>
                    <strong>{periodLabel}</strong>
                    <button type="button" onClick={() => this.moveProgressPeriod(1)} aria-label="Next month" title="Next month">›</button>
                  </div>
                  <label className="pomodoro-progress-zoom">
                    <span>Horizontal zoom</span>
                    <input type="range" min="1" max="8" step="1" value={progressZoom} onChange={this.setProgressZoom} />
                    <output>{progressZoom}×</output>
                  </label>
                </div>

                <div className="pomodoro-progress-chart" aria-label={`Reflection difficulty in ${periodLabel}`}>
                  <svg viewBox={`0 0 ${graphWidth} ${graphHeight}`} role="img" style={{ minWidth: `${graphWidth}px` }}>
                      <text x="14" y="20" className="pomodoro-progress-axis-title">Difficulty</text>
                      {[1, 2, 3, 4, 5].map((score) => {
                        const y = chartBottom - (((score - 1) / 4) * (chartBottom - chartTop));
                        return (
                          <g key={score}>
                            <line x1={chartLeft} y1={y} x2={graphWidth - 16} y2={y} className="pomodoro-progress-grid" />
                            <text x={chartLeft - 16} y={y + 4} textAnchor="middle" className="pomodoro-progress-tick">{score}</text>
                          </g>
                        );
                      })}
                      {Array.from({ length: daysInMonth }, (_, index) => {
                        const day = index + 1;
                        const x = chartLeft + (index * dayWidth) + (dayWidth / 2);
                        return (
                          <g key={day}>
                            <line x1={chartLeft + (index * dayWidth)} y1={chartTop} x2={chartLeft + (index * dayWidth)} y2={chartBottom} className="pomodoro-progress-date-line" />
                            {progressZoom > 1 && Array.from({ length: Math.floor(23 / hourStep) }, (_, hourIndex) => {
                              const hour = (hourIndex + 1) * hourStep;
                              const hourX = chartLeft + (index * dayWidth) + ((hour / 24) * dayWidth);
                              return (
                                <g key={hour}>
                                  <line x1={hourX} y1={chartTop} x2={hourX} y2={chartBottom} className="pomodoro-progress-hour-line" />
                                  <text x={hourX} y="209" textAnchor="middle" className="pomodoro-progress-time-hint">{String(hour).padStart(2, "0")}</text>
                                </g>
                              );
                            })}
                            <text x={x} y="232" textAnchor="middle" className="pomodoro-progress-date">{day}</text>
                          </g>
                        );
                      })}
                      <text x={chartLeft} y="248" className="pomodoro-progress-time-hint">Each day spans 00:00–24:00</text>
                      <polyline
                        points={monthPoints.map((point) => {
                          const position = pointPositions.get(point.id);
                          return `${position.x},${position.y}`;
                        }).join(" ")}
                        className="pomodoro-progress-line"
                      />
                      {monthPoints.map((point, index) => {
                        const position = pointPositions.get(point.id);
                        const isSelected = selectedProgressPoint?.id === point.id;
                        return (
                          <g key={point.id} role="button" tabIndex="0" className="pomodoro-progress-point"
                            aria-label={`Reflection ${index + 1}, ${new Date(point.created_at).toLocaleString()}, difficulty ${point.difficulty_score}`}
                            onClick={() => this.selectProgressPoint(point)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") this.selectProgressPoint(point);
                            }}>
                            <title>{`${new Date(point.created_at).toLocaleString()} · Difficulty ${point.difficulty_score}`}</title>
                            <circle cx={position.x} cy={position.y} r={isSelected ? 5 : 3} className={isSelected ? "is-selected" : ""} />
                          </g>
                        );
                      })}
                  </svg>
                </div>

                {selectedProgressPoint && this.state.loadingReflections.has(selectedProgressPoint.writer_session_id) && (
                  <div className="pomodoro-modal-empty">Loading reflection…</div>
                )}
                {selectedReflection && !this.state.loadingReflections.has(selectedProgressPoint.writer_session_id) && (
                  <ul className="pomodoro-session-list pomodoro-progress-reflection">
                    <PomodoroSessionCard
                      session={selectedSession}
                      reflections={[selectedReflection]}
                      reflectionOnly
                    />
                  </ul>
                )}
              </div>
            )}
          </div>

          {this.state.deletingSessionId && (
            <div className="pomodoro-delete-overlay">
              <div className="pomodoro-delete-dialog">
                <p>Delete this session?</p>
                <div className="pomodoro-delete-actions">
                  <button
                    type="button"
                    className="pomodoro-delete-btn-confirm"
                    onClick={() => this.deleteSession(this.state.deletingSessionId)}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="pomodoro-delete-btn-cancel"
                    onClick={this.cancelDelete}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    );

    if (embedded) {
      return (
        <div className="step-results-scroll summary-side-scroll pomodoro-side-scroll" aria-label="Reflections panel">
          <aside className="step-results-side-panel pomodoro-side-panel">
            {modalBody}
          </aside>
        </div>
      );
    }

    return (
      <div
        className="pomodoro-modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label="Pomodoro session history"
        onClick={this.handleBackdropClick}
      >
        {modalBody}
      </div>
    );
  }
}

export default PomodoroSessionsModal;
